<script module lang="ts">
	export const iconSizeMap = {
		xxs: '8px',
		xs: '12px',
		sm: '16px',
		md: '24px',
		lg: '32px',
		xl: '40px'
	} as const

	export type IconVariant =
		| 'default'
		| 'neutral'
		| 'primary'
		| 'success'
		| 'warning'
		| 'danger'
		| 'muted'
		| 'info'

	// Module-level cache for resolved icon components.
	const iconCache = new Map<
		string,
		import('svelte').Component | typeof import('svelte').SvelteComponent
	>()

	function loadIcon(
		iconName: string
	):
		| Promise<import('svelte').Component | typeof import('svelte').SvelteComponent | null>
		| import('svelte').Component
		| typeof import('svelte').SvelteComponent
		| null {
		if (!(iconName in iconMap)) {
			console.warn(`Icon "${iconName}" not found in iconMap`)
			return null
		}

		const cached = iconCache.get(iconName)
		if (cached) return cached

		return iconMap[iconName as IconName]()
			.then((module) => {
				const Imported = module?.default
				if (typeof Imported === 'function' || typeof Imported === 'object') {
					iconCache.set(iconName, Imported)
					return Imported
				}
				console.error(`Invalid icon module for "${iconName}"`, module)
				return null
			})
			.catch((err) => {
				console.error(`Failed to load icon "${iconName}":`, err)
				return null
			})
	}

	import { iconMap, type IconName } from '../../svg-icons/iconsMap.ts'
</script>

<script lang="ts">
	type IconSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
	type IconVariant =
		| 'default'
		| 'neutral'
		| 'primary'
		| 'success'
		| 'warning'
		| 'danger'
		| 'muted'
		| 'variant'

	const {
		name,
		size = 'md',
		variant = '',
		class: className = '',
		'aria-label': ariaLabel = undefined
	} = $props<{
		name: IconName
		class?: string
		size?: IconSize
		variant?: IconVariant
		'aria-label'?: string
	}>()

	const iconResult = $derived(loadIcon(name))
</script>

{#await iconResult then IconComponent}
	{#if IconComponent}
		<div
			class={['icon-wrapper', className].filter(Boolean).join(' ')}
			class:icon-xxs={size === 'xxs'}
			class:icon-xs={size === 'xs'}
			class:icon-sm={size === 'sm'}
			class:icon-md={size === 'md'}
			class:icon-lg={size === 'lg'}
			class:icon-xl={size === 'xl'}
			class:icon-variant-default={variant === 'default'}
			class:icon-variant-neutral={variant === 'neutral'}
			class:icon-variant-primary={variant === 'primary'}
			class:icon-variant-success={variant === 'success'}
			class:icon-variant-warning={variant === 'warning'}
			class:icon-variant-danger={variant === 'danger'}
			class:icon-variant-muted={variant === 'muted'}
			class:icon-variant-info={variant === 'info'}
		>
			<IconComponent
				height={iconSizeMap[size as keyof typeof iconSizeMap]}
				width={iconSizeMap[size as keyof typeof iconSizeMap]}
				aria-hidden={ariaLabel ? 'false' : 'true'}
				aria-label={ariaLabel}
			/>
		</div>
	{/if}
{/await}

<style lang="postcss">
	.icon-wrapper {
		@apply inline-block flex-none;
	}
	.icon-xxs {
		@apply w-[8px] h-[8px];
	}
	.icon-xs {
		@apply w-[12px] h-[12px];
	}
	.icon-sm {
		@apply w-[16px] h-[16px];
	}
	.icon-md {
		@apply w-[24px] h-[24px];
	}
	.icon-lg {
		@apply w-[32px] h-[32px];
	}
	.icon-xl {
		@apply w-[40px] h-[40px];
	}

	.icon-variant {
		@apply text-gray-400;
	}

	.icon-variant-default {
		@apply text-gray-400;
	}

	.icon-variant-neutral {
		@apply text-gray-400;
	}

	.icon-variant-info {
		@apply text-[#1A74A8CC];
	}

	.icon-variant-primary {
		@apply text-brand;
	}

	.icon-variant-success {
		@apply text-[#067A57];
	}

	.icon-variant-warning {
		@apply text-[#8F6C1A];
	}

	.icon-variant-danger {
		@apply text-[#E4485B];
	}

	.icon-variant-muted {
		@apply text-[rgba(0,44,77,0.50)];
	}

	.icon-variant-info {
		@apply text-[#1A74A8];
	}
</style>
